import { TemplateTypes } from '../../../../../../../lib/models/TemplateTypes';
import { PrefillFields } from '../../../../../../../lib/models/Questionnaire';
import { isLength } from '../../../../../../../lib/util/helpers';
import { getQuestionConditionsFlow } from '../../../utils/end-user-flow-helpers';
import { applyAnswerSelection } from './answer-selection';
import { getProductStatus } from './prefill-status';

const hasPositivePrefillCode = (answer, productStatuses) => {
  const prefillIds = answer?.[PrefillFields.EXTERNAL_PREFILL_IDS];

  return Array.isArray(prefillIds) && prefillIds.some(prefillId => getProductStatus(productStatuses, prefillId) !== null);
};

/**
 * The answers a prefill selects, in the order of the answers. Any positive code of an answer selects it.
 * Single choice keeps the first one; multiple choice keeps the exception group of the first one,
 * so the prefill never produces a combination a click could not.
 */
export function getPrefilledAnswerIds({ answers, multipleChoice, productStatuses }) {
  const positiveAnswers = (Array.isArray(answers) ? answers : []).filter(answer => hasPositivePrefillCode(answer, productStatuses));

  if (!isLength(positiveAnswers)) {
    return [];
  }

  if (!multipleChoice) {
    return [positiveAnswers[0].id];
  }

  const firstGroup = positiveAnswers[0]?.exception?.value;

  return positiveAnswers.filter(answer => answer?.exception?.value === firstGroup).map(answer => answer.id);
}

/** Every element any branch of the question can lead to or show. */
export function getQuestionBranchElementIds(questionFlow) {
  const conditionTargets = (questionFlow?.conditions ?? []).flatMap(condition => [
    condition?.ifGoTo,
    ...(condition?.ifShowElements ?? []),
    condition?.elseIfGoTo,
    ...(condition?.elseIfShowElements ?? []),
  ]);

  return new Set([...conditionTargets, questionFlow?.elseGoTo, ...(questionFlow?.elseShowElements ?? [])].filter(Boolean));
}

/**
 * Unlike a click, the prefill does not reset every product below the question: those may be prefilled products
 * the customer has not reached yet. Only products that the question's flow ties to another branch are reset.
 */
function getInactiveBranchProductElements(question, resultAnswers, pageElements) {
  if (!question?.flow) {
    return [];
  }

  const branchElementIds = getQuestionBranchElementIds(question.flow);
  const activeFlow = getQuestionConditionsFlow(question.flow, question.content?.multipleChoice, resultAnswers);
  const activeElementIds = new Set([activeFlow?.goTo, ...(activeFlow?.showElements ?? [])]);

  return pageElements.filter(
    element => element.templateType === TemplateTypes.PRODUCT && branchElementIds.has(element.id) && !activeElementIds.has(element.id)
  );
}

/**
 * Selects the prefilled answers as a click would on the data level: the answers of the question, its scoring and
 * the products of the inactive branches. Questions are handled in page order, so a later question wins on a shared
 * product, the same as clicking top to bottom. The visible elements of a page are left to the page itself.
 */
export function applyAnswerPrefill({ allPages = [], answersScoring = {}, productStatuses }) {
  const prefilledQuestions = allPages.flatMap(page =>
    (page.elements ?? [])
      .filter(element => element?.templateType === TemplateTypes.QUESTION && Array.isArray(element?.content?.answers))
      .map(question => ({
        pageId: page.id,
        question,
        resultAnswers: getPrefilledAnswerIds({
          answers: question.content.answers,
          multipleChoice: question.content.multipleChoice,
          productStatuses,
        }),
      }))
      .filter(({ resultAnswers }) => isLength(resultAnswers))
  );

  return prefilledQuestions.reduce(
    (state, { pageId, question, resultAnswers }) => {
      const pageElements = state.allPages.find(page => page.id === pageId)?.elements ?? [];
      const { allPages: updatedPages, answersScoring: updatedScoring } = applyAnswerSelection({
        ...state,
        pageId,
        questionId: question.id,
        resultAnswers,
        resultScoring: question.content.answers.filter(answer => resultAnswers.includes(answer.id)),
        nonFlowProductElements: getInactiveBranchProductElements(question, resultAnswers, pageElements),
      });

      return { allPages: updatedPages, answersScoring: updatedScoring };
    },
    { allPages, answersScoring }
  );
}
